import React, { useState, useContext } from 'react';
import Select from '../common/Select';
import { ContextMenu } from '../common/ContextMenu';
import { MarkedEditor } from '../common/Marked';
import { DividedRow, Full, Quarter, Third } from '../common/RowLengths';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import Errors from '../common/Errors';
import { newMailerDefaults } from './constants/mailerConstants';
import MailerFieldChooserModal from './mailer-body-inject/MailerFieldChooserModal';
import { MailerContext } from '../../hooks/Contexts';
import { defaultChooserState } from './constants/mailerConstants';
import { handleCreateMailer } from './constants/handleMailer';
import { sortByAlpha } from '../../helpers/sorters';
import Icon from '../common/Icon';

const NewMailer = ({ setMailers, toggleCreating }) => {
  const { form, fieldsets, metadata_entries, related_forms } =
    useContext(MailerContext);
  const defaultForm = { form, fieldsets, metadata_entries };
  const tokenizableInputs = ['subject', 'from', 'body', 'pdf_body'];
  const [selectableEntries, setSelectableEntries] = useState([]);
  const [isInjecting, setIsInjecting] = useState(false);
  const [chooserState, setChooserState] = useState({
    selectedForm: defaultForm,
    ...defaultChooserState,
  });
  const [insertionPoint, setInsertionPoint] = useState({
    position: 0,
    name: '',
  });
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: newMailerDefaults,
    criteriaMode: 'all',
  });

  function resetAndToggle(e) {
    e.preventDefault();
    reset(newMailerDefaults);
    toggleCreating();
  }

  function determineMeta(fieldId) {
    let entries = metadata_entries.filter((me) => me.field_id == fieldId);
    setSelectableEntries(entries);
  }

  function insertAttr(category, attr, id, relatedFormID = null) {
    let body = getValues(insertionPoint.name);
    let n = insertionPoint.position;

    if (category === 'custom') {
      let type = insertionPoint.name == 'body' ? 'body' : 'pdf';
      let customCount = 0;
      customCount = (body.match(/{{custom./g) || []).length;
      attr = type + '.' + (customCount + 1).toString();
    } else {
      category = id ? category + `.${id}` : category;
      category = relatedFormID
        ? `related_form.${relatedFormID}.` + category
        : category;
      if (attr.indexOf('Meta: ') != -1) {
        // Remove 'Meta: ' when inserting metadata token
        attr = attr.substr(6);
        if (
          category == 'form' ||
          (relatedFormID && category == `related_form.${relatedFormID}.form`)
        ) {
          // Form metadata requires 'meta.'
          attr = 'meta.' + attr;
        }
      }
    }

    let formattedAttr = '{{' + category + `.${attr}` + '}}';
    body = body.slice(0, n) + formattedAttr + body.slice(n);

    setValue(insertionPoint.name, body, { shouldDirty: true });
    setInsertionPoint((state) => {
      return { ...state, position: n + formattedAttr.length };
    });
  }

  function insertChildAttr(token) {
    let body = getValues(insertionPoint.name);
    let n = insertionPoint.position;
    body = body.slice(0, n) + token + body.slice(n);
    setValue(insertionPoint.name, body, { shouldDirty: true });
    setInsertionPoint((state) => {
      return { ...state, position: n + token.length };
    });
  }

  function setupInject(mailerBodyType, chosenForm) {
    let el = document.getElementsByName(mailerBodyType);
    setChooserState({ selectedForm: chosenForm, ...defaultChooserState });
    setIsInjecting(true);
    setInsertionPoint({ position: el[0].selectionStart, name: mailerBodyType });
  }

  const toggleTipElement = (e, elementId, show) => {
    e.preventDefault();
    let el = document.getElementById(elementId);
    el.style.display = show ? 'block' : 'none';
  };
  const defaultTipStyle = {
    display: 'none',
    fontSize: '14px',
    marginLeft: '6px',
  };

  const onSubmit = async (formData) => {
    let newMailer = await handleCreateMailer({ ...formData, form_id: form.id });
    if (newMailer.ok) {
      setMailers((mailers) =>
        sortByAlpha([...mailers, newMailer.data], 'name')
      );
      toggleCreating();
      reset(newMailerDefaults);
    } else if (newMailer.errors) {
      Object.entries(newMailer.errors).forEach(([key, message]) =>
        setError(key, { types: { [key]: message } })
      );
    }
  };

  const contextMenuOptions = (target) => (
    <>
      <li
        key={`default-${target}`}
        onClick={(e) => setupInject(target, defaultForm)}
      >
        Insert Field
      </li>
      {related_forms && (
        <>
          {related_forms
            .filter((f) => f.form.is_parent)
            .map((f) => (
              <li
                key={`parent-${target}`}
                onClick={(e) => setupInject(target, f)}
              >
                Insert Parent Fields: {f.form.name}
              </li>
            ))}
          {related_forms
            .filter((f) => f.form.submission_validate)
            .map((f) => (
              <li
                key={`child-${target}-${f.form.id}`}
                onClick={(e) => setupInject(target, f)}
              >
                Insert Child Fields: {f.form.name}
              </li>
            ))}
        </>
      )}
    </>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <fieldset>
        <MailerFieldChooserModal
          insertAttr={insertAttr}
          defaultForm={defaultForm}
          chooserState={chooserState}
          setChooserState={setChooserState}
          isInjecting={isInjecting}
          setIsInjecting={setIsInjecting}
          insertChildAttr={insertChildAttr}
        />
        {tokenizableInputs.map((property) => (
          <ContextMenu
            target={property}
            menuOptions={contextMenuOptions(property)}
            key={`context-menu-${property}`}
          />
        ))}

        <DividedRow>
          <Quarter hasCheckbox labelFor='on-submit' title='On Submit:'>
            <input id='on-submit' type='checkbox' {...register('on_submit')} />
          </Quarter>
        </DividedRow>

        <DividedRow>
          <Full title='Name:'>
            <input
              type='text'
              placeholder='Mailer name...'
              {...register('name', { required: 'Required' })}
            />
            <ErrorMessage
              errors={errors}
              name='name'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Full>
        </DividedRow>

        <DividedRow>
          <Full title='From:'>
            <input
              type='text'
              placeholder='txssc@txstate.edu'
              {...register('from')}
              onFocus={(e) => toggleTipElement(e, 'from-tip', true)}
              onBlur={(e) => toggleTipElement(e, 'from-tip', false)}
            />
            <i id='from-tip' style={defaultTipStyle}>
              Shift + Click to insert data
            </i>
            <ErrorMessage
              errors={errors}
              name='from'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Full>
        </DividedRow>

        <DividedRow>
          <Third title='To Field Value:'>
            <div className='select-input'>
              <select
                {...register('field_id', {
                  required: 'Required',
                  onChange: (e) => determineMeta(e.target.value),
                })}
              >
                <option value=''>Select Field...</option>
                {fieldsets.map((fs, i) => {
                  return (
                    <optgroup
                      key={`fs-${fs.fieldset.id}-to`}
                      label={fs.fieldset.name}
                    >
                      {fs.fields.map((f, i) => {
                        return (
                          <option
                            value={f.id}
                            key={`${f.id}-to`}
                            disabled={!['text', 'select'].includes(f.type)}
                          >
                            {f.name}
                          </option>
                        );
                      })}
                    </optgroup>
                  );
                })}
              </select>
            </div>
            <ErrorMessage
              errors={errors}
              name='field_id'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Third>

          {selectableEntries.length > 0 && (
            <Third title='To Meta Entry Value:'>
              <Select
                content={[
                  { label: 'Select entry...', value: null },
                  ...selectableEntries.map((me) => {
                    return { label: me.title, value: me.id };
                  }),
                ]}
                register={{
                  ...register('metadata_entry_id', {
                    shouldUnregister: true,
                  }),
                }}
              />
            </Third>
          )}
          <Third title='Calendar Field:'>
            <div className='select-input'>
              <select {...register('calendar_event_field_id')}>
                <option value=''>None...</option>
                {fieldsets.map((fs, i) => {
                  return (
                    <optgroup
                      key={`fs-${fs.fieldset.id}-cal`}
                      label={fs.fieldset.name}
                    >
                      {fs.fields.map((f, i) => {
                        return (
                          <option
                            value={f.id}
                            key={`${f.id}-cal`}
                            disabled={!['radio', 'select'].includes(f.type)}
                          >
                            {f.name}
                          </option>
                        );
                      })}
                    </optgroup>
                  );
                })}
              </select>
            </div>
            <ErrorMessage
              errors={errors}
              name='calendar_event_field_id'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Third>
        </DividedRow>

        <DividedRow>
          <Full title='Subject:'>
            <input
              type='text'
              placeholder='Mailer Subject...'
              {...register('subject', { required: 'Required' })}
              onFocus={(e) => toggleTipElement(e, 'subject-tip', true)}
              onBlur={(e) => toggleTipElement(e, 'subject-tip', false)}
            />
            <i id='subject-tip' style={defaultTipStyle}>
              Shift + Click to insert data
            </i>
            <ErrorMessage
              errors={errors}
              name='subject'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Full>
        </DividedRow>

        <DividedRow>
          <Full title='Body:'>
            <MarkedEditor
              register={{
                ...register('body', {
                  required: 'Required',
                }),
              }}
              placeholder='Mailer body...'
              getValues={() => getValues('body')}
              onFocus={(e) => toggleTipElement(e, 'body-tip', true)}
              onBlur={(e) => toggleTipElement(e, 'body-tip', false)}
            />
            <i id='body-tip' style={defaultTipStyle}>
              Shift + Click to insert data
            </i>
            <ErrorMessage
              errors={errors}
              name='body'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Full>
        </DividedRow>

        <DividedRow>
          <Full title='PDF Body:'>
            <MarkedEditor
              register={{
                ...register('pdf_body'),
              }}
              placeholder='PDF Content...'
              getValues={() => getValues('pdf_body')}
              onFocus={(e) => toggleTipElement(e, 'pdf_body-tip', true)}
              onBlur={(e) => toggleTipElement(e, 'pdf_body-tip', false)}
            />
            <i id='pdf_body-tip' style={defaultTipStyle}>
              Shift + Click to insert data
            </i>
          </Full>
        </DividedRow>

        <DividedRow>
          <Quarter hasCheckbox title='Landscape:' labelFor='landscape'>
            <input type='checkbox' id='landscape' {...register('landscape')} />
          </Quarter>
        </DividedRow>
      </fieldset>
      <div className='controls'>
        <button className='single' type='submit'>
          Save
          <Icon icon='save' />
        </button>
        <button onClick={(e) => resetAndToggle(e)} className='blue'>
          Cancel
          <Icon icon='xmark' />
        </button>
      </div>
    </form>
  );
};

export default NewMailer;
